import React, { MutableRefObject, ReactNode } from "react";

import { useRouter } from "next/router";

import {
  Column,
  dynamicColumns,
  IFilter,
  Record,
  SchemaResponse,
  SortOrder,
  Table as GSMValveTable,
  transformFilters,
  useSchemaParser,
} from "@gsmvalve/gsmvalve-table";
import { EmptyState, QuickFilters, TableControls } from "@gsmvalve/gsmvalve-table/dist/cjs/export";
import { t, Trans } from "@lingui/macro";
import { Col, Empty, Row, Typography } from "antd";
import { findIndex } from "lodash";
import styled, { useTheme } from "styled-components";
import { SWRConfiguration } from "swr";

import { TabFilter } from "@gsmvalve/gsmvalve-table/dist/cjs/components/filter";
import { useLocaleContext } from "components/language-wrapper";
import { LoadingIndicator } from "components/loading";
import { TableContainer } from "components/table/styles";
import TableSearch from "components/table/table-search";

import { useApplicationContext } from "contexts/app";

import { DATA_TYPE } from "@gsmvalve/gsmvalve-table/dist/cjs/types";

import EmptyTableIcon from "icons/empty-table-icon";

import { API_VERSION } from "utils/http-client-utils";

export const LAYOUT = {
  GRID: "grid",
  TABLE: "table",
};

const EmptyContainer = styled.div<{ height: number }>`
  padding: 24px;
  min-height: ${(props) => props.height}px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const EmptyTable = ({
  message = t`There is currently no data available to display based on filters or search used`,
  height = 0,
  extra,
}: {
  message?: string;
  height?: number;
  extra?: ReactNode;
}) => {
  return (
    <EmptyContainer height={height}>
      <Empty
        image={<EmptyTableIcon />}
        description={
          <Row align={"middle"} gutter={[16, 16]}>
            <Col span={24}>
              <Typography.Title level={4}>
                <Trans>No data to display</Trans>
              </Typography.Title>
            </Col>
            <Col span={24}>
              <Typography.Text>{message}</Typography.Text>
            </Col>
            <Col span={24}>{extra}</Col>
          </Row>
        }
      />
    </EmptyContainer>
  );
};

export const pageCountMessage = (msg: string) => (count?: number) => {
  return count === undefined ? null : <Typography.Text strong>{count + " " + msg}</Typography.Text>;
};

interface TableEmptyState extends EmptyState {
  withDescription?: string;
}

interface Props<T> {
  id: string;
  columns: {
    filtersPath: string;
    path: string;
    customFilters?: IFilter[];
    customColumns?: Column[];
    quickFilters?: QuickFilters[];
  };
  records: {
    url: string;
    limit?: number;
    refreshInterval?: number;
    modifier?: (data: any, refreshTable?: () => void) => Record[];
    sort: SortOrder[];
    fetcherProps?: SWRConfiguration<any>;
  };
  headerActions?: ReactNode[];
  rowActions?: { label: string; action: (data: any) => void }[];
  onRow?: (data: any) => void;
  tabsFilter?: TabFilter;
  disableControls?: boolean;
  offset?: number;
  mutateTable?: MutableRefObject<any>;
  callback?: MutableRefObject<any>;
  showFilterBtn?: boolean;
  controls?: TableControls;
  gridRender?: (data: any) => ReactNode;
  onStateChange?: (event: any) => void;
  renderItemWidth?: number;
  empty?: TableEmptyState;
  rowLayout?: string;
}

const Table = <T extends object>({
  id,
  columns,
  records,
  headerActions,
  rowActions,
  onRow,
  tabsFilter,
  disableControls = false,
  offset = 95,
  mutateTable,
  callback,
  controls,
  gridRender,
  onStateChange,
  renderItemWidth,
  empty,
  rowLayout,
}: Props<T>) => {
  const router = useRouter();
  const { httpClient, language } = useApplicationContext();
  const tokens = useTheme();
  const { antdLocale } = useLocaleContext();

  const { search } = router.query;

  const schema: SchemaResponse<T> | any = useSchemaParser({
    data: require(`types/openapi.${API_VERSION}.json`),
    rootKey: columns.path,
    filtersKey: columns.filtersPath,
  });
  const omitFields = { key: "id", omit: true };
  const fetchURL = `${httpClient.basePath}${records.url}`;
  const tableColumns = dynamicColumns<T>(schema?.columns, [...(columns?.customColumns || []), omitFields]);

  const tableFilters = transformFilters(schema?.filters, columns.customFilters || []);

  if (!tableColumns) {
    return <LoadingIndicator />;
  }

  const getKey = httpClient.getKey as (url: string) => (_: number, prev: any) => string;

  return (
    <TableContainer disableheader={disableControls}>
      <GSMValveTable
        id={id}
        columns={tableColumns}
        filters={tableFilters}
        url={fetchURL}
        defaultSort={records.sort}
        getKey={getKey}
        limit={records.limit || 100}
        modifier={records.modifier}
        sortable={"multiple"}
        rowActions={rowActions}
        mutateTable={mutateTable}
        httpClientGet={httpClient.swrInfiniteFetcher}
        router={router}
        headerActions={headerActions}
        lang={language}
        searchSize={"middle"}
        onRow={onRow}
        fetcherProps={{
          fetchingRecordsMsg: t`Loading...`,
          refreshInterval: records?.refreshInterval as any,
          revalidateFirstPage: true,
          revalidateOnMount: true,
          ...(records?.fetcherProps && { ...records.fetcherProps }),
        }}
        offsetTop={offset}
        tabsFilter={tabsFilter}
        searchText={search?.toString()}
        callback={callback}
        controls={controls}
        gridRender={gridRender}
        renderItemWidth={renderItemWidth}
        onStateChange={onStateChange}
        // @ts-ignore
        empty={{
          ...empty,
          emptyWithFilter: (
            <EmptyTable
              height={window.innerHeight - offset}
              extra={empty?.emptyWithFilter}
              message={empty?.withDescription}
            />
          ),
        }}
        defaultLayout={rowLayout as "table" | "grid"}
        quickFilters={columns.quickFilters}
        tokens={tokens}
        antdLocale={antdLocale}
        defaultColumWidth={100}
      />
    </TableContainer>
  );
};

export default Table;

const fieldTypes: { [key: string]: DATA_TYPE } = {
  field: DATA_TYPE.TEXT,
  string: DATA_TYPE.TEXT,
  datetime: DATA_TYPE.DATETIME,
  list: DATA_TYPE.ARRAY,
  choice: DATA_TYPE.CHOICE,
};

interface OptionsColumn {
  label: string;
  type: string;
  choices: { value: string; display_name: string }[];
  child: OptionsColumn;
}

const mapOptionsToFilters = (options: { [key: string]: OptionsColumn }, filters: IFilter[]) => {
  const responses: IFilter[] = [];

  options &&
    Object.keys(options).forEach((option) => {
      const fieldType = options[option].type;
      const field = fieldTypes[fieldType];

      const response: IFilter = {
        key: option,
        title: options[option].label,
        type: field,
      };

      switch (field) {
        case DATA_TYPE.CHOICE:
          const values = options[option]?.choices?.map((row) => {
            return {
              label: row.display_name,
              value: row.value,
            };
          });

          response["type"] = DATA_TYPE.TEXT;
          if (values) {
            response["type"] = field;
            response["options"] = values;
          }
          break;
        case DATA_TYPE.ARRAY:
          if (options[option].child) {
            if (options[option].child.type === DATA_TYPE.CHOICE) {
              const values = options[option]?.child?.choices?.map((row) => {
                return {
                  label: row.display_name,
                  value: row.value,
                  name: row.display_name,
                };
              });
              if (values) {
                response["type"] = DATA_TYPE.CHOICE;
                response["options"] = values;
              }
            }
          }
          break;
      }
      responses.push(response);
    });

  filters.forEach((filter) => {
    const index = findIndex(responses, (r) => r.key == filter.key);
    if (index !== -1) {
      responses[index] = filter;
    } else {
      responses.push(filter);
    }
  });

  return responses;
};
const exclude = ["account", "sideload", "count", "ordering", "page_size"];
const onHasFilters = (moreExclude?: string[]) => (url: any) => {
  let exist = false;
  url.searchParams.forEach((_: any, k: string) => {
    const keys = moreExclude ? exclude.concat(moreExclude) : exclude;

    if (!keys.includes(k)) {
      exist = true;
    }
  });

  return exist;
};

export { mapOptionsToFilters, onHasFilters, TableSearch };
